import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useProfile } from '../hooks/useProfile';
import ConfirmDialog from './ConfirmDialog';
import AttendanceModal from './AttendanceModal';
import BestSalesPerson from './BestSalesPerson';
import NotificationsPanel from './NotificationsPanel';
import Handler from './Handler';
import { stockAPI, productsAPI } from '../services/api';
import './attendanceModal.css';

const StaffDashboard = ({ onNavigate, onLogout, userData, currentPage }) => {
  const { profile, avatarUrl, initials } = useProfile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeNav, setActiveNav] = useState('home');
  const [isHandler, setIsHandler] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceType, setAttendanceType] = useState(null); // 'checkin' or 'checkout'
  const [attendanceStatus, setAttendanceStatus] = useState('Not checked in');
  const [checkInTime, setCheckInTime] = useState(null);
  const [checkOutTime, setCheckOutTime] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [dashboardStats, setDashboardStats] = useState({
    stockInCount: 0,
    stockOutCount: 0,
    totalProducts: 0,
    highestSellingProduct: null,
    loading: true
  });
  const [handlerExplicitlySelected, setHandlerExplicitlySelected] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Fetch today's attendance status and notifications
  useEffect(() => {
    const fetchTodayAttendance = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/attendance/today?username=${userData.username || ''}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.attendance) {
            setCheckInTime(data.attendance.check_in_time);
            setCheckOutTime(data.attendance.check_out_time);
            if (data.attendance.check_out_time) {
              setAttendanceStatus('Checked out');
            } else if (data.attendance.check_in_time) {
              setAttendanceStatus('Checked in');
            }
          }
        }
      } catch (err) {
        console.error('Error fetching attendance:', err);
      }
    };

    const fetchNotifications = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        if (userData.id) {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/notifications?userId=${userData.id}&userType=staff`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
            }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setNotifications(data.notifications || []);
              setUnreadCount(data.notifications.filter(n => !n.is_read).length);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching notifications:', err);
      }
    };

    fetchTodayAttendance();
    fetchNotifications();
    // Refresh notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    
    // Listen for notification updates
    const handleNotificationUpdate = () => {
      console.log('🔔 Staff: Notification update event received, refreshing...');
      fetchNotifications();
    };
    window.addEventListener('notificationsUpdated', handleNotificationUpdate);
    window.addEventListener('notificationSent', handleNotificationUpdate);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('notificationsUpdated', handleNotificationUpdate);
      window.removeEventListener('notificationSent', handleNotificationUpdate);
    };
  }, []);

  // Fetch dashboard statistics
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setDashboardStats(prev => ({ ...prev, loading: true }));
        
        // Fetch stock stats
        const stockStats = await stockAPI.getDashboardStats();
        
        // Fetch total products count
        const productsData = await productsAPI.getAll();
        const totalProducts = productsData.products?.length || 0;
        
        setDashboardStats({
          stockInCount: stockStats.stockInCount || 0,
          stockOutCount: stockStats.stockOutCount || 0,
          totalProducts: totalProducts,
          highestSellingProduct: stockStats.fastestSelling || null,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        setDashboardStats(prev => ({ ...prev, loading: false }));
      }
    };

    fetchDashboardStats();
    // Refresh stats every 5 minutes
    const statsInterval = setInterval(fetchDashboardStats, 300000);
    
    return () => {
      clearInterval(statsInterval);
    };
  }, []);

  // Check if user is a handler
  useEffect(() => {
    const checkHandlerStatus = async () => {
      try {
        const storedUserData = JSON.parse(localStorage.getItem('userData') || '{}');
        const userId = storedUserData.id || userData?.id;
        
        if (userId) {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/staff/${userId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
            }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.staff) {
              setIsHandler(data.staff.is_handler === true);
            }
          }
        }
      } catch (err) {
        console.error('Error checking handler status:', err);
      }
    };
    
    checkHandlerStatus();
  }, [userData]);

  // Sync activeNav with currentPage
  useEffect(() => {
    // If handler is explicitly selected, keep it active (handler is now in master menu)
    if (handlerExplicitlySelected && activeNav === 'handler') {
      // Handler is explicitly selected, keep it active
      return;
    }

    // Reset handler flag when navigating to a different page (but not when clicking master menu)
    if (currentPage !== 'staffHome' && currentPage !== 'handler' && currentPage !== 'masterMenu' && 
        currentPage !== 'products' && currentPage !== 'suppliers' && currentPage !== 'transport' && 
        currentPage !== 'chitPlans' && currentPage !== 'addChitCustomer' && currentPage !== 'chitPlanMaster' && 
        currentPage !== 'addChitPlan' && currentPage !== 'categoryMaster') {
      setHandlerExplicitlySelected(false);
    }

    if (currentPage === 'staffHome') {
      // Only set to home if handler wasn't explicitly selected
      if (!handlerExplicitlySelected) {
        setActiveNav('home');
      }
    } else if (currentPage === 'handler') {
      setActiveNav('handler');
      setHandlerExplicitlySelected(true);
    } else if (currentPage === 'customers') {
      setActiveNav('customers');
    } else if (currentPage === 'products' || currentPage === 'suppliers' || currentPage === 'transport' || 
               currentPage === 'chitPlans' || currentPage === 'addChitCustomer' || currentPage === 'chitPlanMaster' || 
               currentPage === 'addChitPlan' || currentPage === 'categoryMaster' || currentPage === 'masterMenu') {
      // Only set to masterMenu if handler isn't active
      if (activeNav !== 'handler') {
        setActiveNav('masterMenu');
      }
    } else if (currentPage === 'dispatch' || currentPage === 'transactionMenu' || currentPage === 'stockIn' || 
               currentPage === 'stockOut' || currentPage === 'createSupplier' || currentPage === 'supplierTransactionMaster' || 
               currentPage === 'transactionProducts' || currentPage === 'addProductPricing' || currentPage === 'services' ||
               currentPage === 'salesOrder' || currentPage === 'chitEntryMaster' || currentPage === 'purchaseOrderMaster' ||
               currentPage === 'paymentMaster' || currentPage === 'quotationMaster') {
      setActiveNav('transactionMenu');
    } else if (currentPage === 'settings') {
      setActiveNav('settings');
    }
  }, [currentPage, handlerExplicitlySelected, activeNav]);

  const workingStore = useMemo(() => {
    return profile?.primary_store || 'Hyderabad Store';
  }, [profile]);

  const shiftLabel = '9:00 AM – 6:00 PM';

  const handleNavClick = (navItem, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setActiveNav(navItem);
    if (!onNavigate) return;
    if (navItem === 'home') {
      onNavigate('staffHome');
    } else if (navItem === 'handler') {
      // Handle handler as internal state, don't navigate away
      // Mark handler as explicitly selected so it stays active when navigating to other pages
      setHandlerExplicitlySelected(true);
    } else if (navItem === 'customers') {
      onNavigate('customers');
    } else if (navItem === 'masterMenu') {
      // Handle masterMenu as internal state, don't navigate away
      // Just update the activeNav state, which is already done above
    } else if (navItem === 'transactionMenu') {
      // Handle transactionMenu as internal state, don't navigate away
      // Just update the activeNav state, which is already done above
    } else if (navItem === 'settings') {
      onNavigate('settings');
    }
    setSidebarOpen(false);
    setMenuOpen(false);
  };

  const handleCheckIn = () => {
    setAttendanceType('checkin');
    setShowAttendanceModal(true);
  };

  const handleCheckOut = () => {
    setAttendanceType('checkout');
    setShowAttendanceModal(true);
  };

  const handleAttendanceSuccess = (type, time, message, warning) => {
    if (type === 'checkin') {
      setCheckInTime(time);
      setAttendanceStatus('Checked in');
    } else if (type === 'checkout') {
      setCheckOutTime(time);
      setAttendanceStatus('Checked out');
    }
    setShowAttendanceModal(false);
    setAttendanceType(null);
    
    // Display success message in-app
    setSuccessMessage(message || `${type === 'checkin' ? 'Checked in' : 'Checked out'} successfully`);
    if (warning) {
      setWarningMessage(warning);
    }
    
    // Auto-hide messages after 5 seconds
    setTimeout(() => {
      setSuccessMessage('');
      setWarningMessage('');
    }, 5000);
    
    // Refresh notifications if there's a warning
    if (warning) {
      setTimeout(() => {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        if (userData.id) {
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/notifications?userId=${userData.id}&userType=staff`)
            .then(res => res.json())
            .then(data => {
              if (data.success) {
                setNotifications(data.notifications || []);
                setUnreadCount(data.notifications.filter(n => !n.is_read).length);
              }
            })
            .catch(err => console.error('Error refreshing notifications:', err));
        }
      }, 1000);
    }
  };

  const handleAttendanceClose = () => {
    setShowAttendanceModal(false);
    setAttendanceType(null);
  };

  return (
    <div className="dashboard-container">
      {/* Left Sidebar Navigation */}
      <nav className={`sidebar-nav ${sidebarOpen ? 'open' : ''}`}>
        <div 
          className={`nav-item ${activeNav === 'home' ? 'active' : ''}`} 
          onClick={(e) => handleNavClick('home', e)}
        >
          <div className="nav-icon">
            <i className="fas fa-home"></i>
          </div>
          <span>Home</span>
        </div>
        <div 
          className={`nav-item ${activeNav === 'masterMenu' ? 'active' : ''}`} 
          onClick={(e) => handleNavClick('masterMenu', e)}
        >
          <div className="nav-icon">
            <i className="fas fa-th-large"></i>
          </div>
          <span>Master Menu</span>
        </div>
        <div 
          className={`nav-item ${activeNav === 'transactionMenu' ? 'active' : ''}`} 
          onClick={(e) => handleNavClick('transactionMenu', e)}
        >
          <div className="nav-icon">
            <i className="fas fa-exchange-alt"></i>
          </div>
          <span>Transaction</span>
        </div>
        <div 
          className={`nav-item ${activeNav === 'settings' ? 'active' : ''}`} 
          onClick={(e) => handleNavClick('settings', e)}
        >
          <div className="nav-icon">
            <i className="fas fa-cog"></i>
          </div>
          <span>Settings</span>
        </div>
      </nav>

      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}

      {/* Main */}
      <div className={`dashboard-main ${sidebarOpen ? 'shifted' : ''}`}>
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <button className="sidebar-toggle" onClick={() => setSidebarOpen((p) => !p)}>
              <i className="fas fa-bars"></i>
            </button>
            <div className="admin-icon" style={{ position: 'relative', overflow: 'hidden' }}>
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const span = e.target.nextSibling;
                    if (span) span.style.display = 'flex';
                  }}
                />
              ) : null}
              <span
                style={{
                  display: avatarUrl ? 'none' : 'flex',
                  position: avatarUrl ? 'absolute' : 'relative',
                  zIndex: avatarUrl ? 0 : 1,
                }}
              >
                {initials}
              </span>
            </div>
            <div className="header-title">
              <h1>Staff Panel</h1>
              <p>Anitha Stores</p>
            </div>
          </div>
          <div className="header-right">
            <div 
              className="notification-icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🔔 Staff: Bell clicked, opening notifications panel');
                setShowNotificationsPanel(true);
              }}
              style={{ cursor: 'pointer', position: 'relative', zIndex: 10 }}
              title="View Notifications"
            >
              <i className="fas fa-bell"></i>
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </div>
            <div className="menu-icon-container" ref={menuRef}>
              <div className="menu-icon" onClick={() => setMenuOpen((p) => !p)}>
                <i className="fas fa-bars"></i>
              </div>
              {menuOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-item" onClick={() => handleNavClick('settings')}>
                    <i className="fas fa-cog"></i>
                    <span>Settings</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-item logout" onClick={onLogout}>
                    <i className="fas fa-sign-out-alt"></i>
                    <span>Logout</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="dashboard-content">
          {/* Success and Warning Messages */}
          {successMessage && (
            <div style={{ 
              padding: '16px', 
              background: '#d4edda', 
              color: '#155724', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #c3e6cb',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <i className="fas fa-check-circle" style={{ fontSize: '20px' }}></i>
              <span style={{ fontWeight: '500' }}>{successMessage}</span>
            </div>
          )}

          {warningMessage && (
            <div style={{ 
              padding: '16px', 
              background: '#fff3cd', 
              color: '#856404', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #ffeaa7',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '20px' }}></i>
              <span style={{ fontWeight: '500' }}>{warningMessage}</span>
            </div>
          )}

          {activeNav === 'masterMenu' ? (
            <div className="master-menu-grid">
              {[
                { title: 'Transport Master', desc: 'Transport partners & routes', icon: 'fa-truck-moving', target: 'transport' },
                { title: 'Category Master', desc: 'Organize product categories', icon: 'fa-tags', target: 'categoryMaster' },
                { title: 'Products', desc: 'Catalog and pricing', icon: 'fa-box', target: 'products' },
                { title: 'Supply Master', desc: 'Suppliers and logistics', icon: 'fa-truck', target: 'suppliers' },
                { title: 'Customers', desc: 'Customer management & details', icon: 'fa-user-friends', target: 'customers' },
                { title: 'Chit Plan', desc: 'Create customers with chit plans', icon: 'fa-user-check', target: 'chitPlan' },
                ...(isHandler ? [{ title: 'Handler', desc: 'View assigned services and orders', icon: 'fa-tools', target: 'handler', isHandler: true }] : []),
              ].map((item) => (
                <div
                  key={item.title}
                  className="stat-card"
                  style={{ cursor: (item.target || item.isHandler) ? 'pointer' : 'default' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (item.isHandler) {
                      // Handle handler as internal state, don't navigate away
                      setActiveNav('handler');
                      setHandlerExplicitlySelected(true);
                    } else if (item.target && onNavigate) {
                      onNavigate(item.target);
                    }
                  }}
                >
                  <div className="stat-content">
                    <h3 className="stat-title">{item.title}</h3>
                    <p className="stat-subtitle">{item.desc}</p>
                  </div>
                  <div className="stat-icon" style={{ backgroundColor: '#dc354520', color: '#dc3545' }}>
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                </div>
              ))}
            </div>
          ) : activeNav === 'transactionMenu' ? (
            <div className="master-menu-grid">
              {[
                { title: 'Dispatch Department', desc: 'Manage dispatch workflows', icon: 'fa-shipping-fast', target: 'dispatch' },
                { title: 'Stock In', desc: 'Record stock entries', icon: 'fa-box-open', target: 'stockInMaster' },
                { title: 'Stock Out', desc: 'Record stock exits', icon: 'fa-box', target: 'stockOutMaster' },
                { title: 'Services', desc: 'Manage service transactions', icon: 'fa-cog', target: 'services' },
                { title: 'Sales Order', desc: 'View and manage sales orders', icon: 'fa-chart-line', target: 'salesOrder' },
                { title: 'Chit Receipt', desc: 'Record chit payments', icon: 'fa-file-invoice-dollar', target: 'chitEntryMaster' },
                { title: 'Purchase Order', desc: 'Create and manage purchase orders', icon: 'fa-shopping-cart', target: 'purchaseOrderMaster' },
                { title: 'Payment Menu', desc: 'Manage payment records', icon: 'fa-money-bill-wave', target: 'paymentMaster' },
                { title: 'Quotations', desc: 'Create and manage quotations', icon: 'fa-file-invoice', target: 'quotationMaster' },
              ].map((item) => (
                <div
                  key={item.title}
                  className="stat-card"
                  style={{ cursor: item.target ? 'pointer' : 'default' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (item.target && onNavigate) {
                      onNavigate(item.target);
                    }
                  }}
                >
                  <div className="stat-content">
                    <h3 className="stat-title">{item.title}</h3>
                    <p className="stat-subtitle">{item.desc}</p>
                  </div>
                  <div className="stat-icon" style={{ backgroundColor: '#dc354520', color: '#dc3545' }}>
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                </div>
              ))}
            </div>
          ) : activeNav === 'handler' ? (
            <Handler 
              onBack={() => {
                setActiveNav('masterMenu');
                setHandlerExplicitlySelected(false);
              }} 
              onNavigate={onNavigate}
              userData={userData}
              inline={true}
            />
          ) : (
            <>
              <div className="controls-section">
                <div className="scope-button">
                  <i className="fas fa-store"></i>
                  <span>Working at</span>
                  <span className="scope-value">{workingStore}</span>
                </div>
                <div className="sales-report-button" style={{ background: 'var(--card-bg)', color: 'var(--text-primary)', boxShadow: '0 2px 4px var(--shadow-light)', border: '1px solid var(--border-color)' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <span className="shift-dot" style={{ width: 10, height: 10, borderRadius: '50%', background: '#35c759' }}></span>
                    Shift {shiftLabel}
                  </span>
                </div>
                <div className="search-bar">
                  <i className="fas fa-search"></i>
                  <input type="text" placeholder="Search tasks, products, inventory..." />
                </div>
              </div>

              <div className="panel checkin-card" style={{ marginBottom: '30px' }}>
                <div className="panel-header">
                  <div>
                    <div className="panel-title">Check-in / Check-out</div>
                    <div className="panel-subtitle">Use face recognition to log your working hours.</div>
                  </div>
                  <div className="status-face">
                    <i className="fas fa-smile"></i>
                  </div>
                </div>
                <div className="panel-row">
                  <div className="label">Current status</div>
                  <div className={`status-pill ${attendanceStatus === 'Checked in' ? 'success' : attendanceStatus === 'Checked out' ? 'info' : 'danger'}`}>
                    <span className="dot"></span>
                    <span>{attendanceStatus}</span>
                  </div>
                </div>
                {checkInTime && (
                  <div className="panel-row">
                    <div className="label">Check-in time</div>
                    <div className="value">{new Date(checkInTime).toLocaleTimeString()}</div>
                  </div>
                )}
                {checkOutTime && (
                  <div className="panel-row">
                    <div className="label">Check-out time</div>
                    <div className="value">{new Date(checkOutTime).toLocaleTimeString()}</div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                  {!checkInTime && (
                    <button className="primary-action" onClick={handleCheckIn} style={{ flex: 1 }}>
                      Check In
                    </button>
                  )}
                  {checkInTime && !checkOutTime && (
                    <button className="primary-action" onClick={handleCheckOut} style={{ flex: 1, background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)' }}>
                      Check Out
                    </button>
                  )}
                </div>
              </div>

              {/* Best Sales Person of the Month */}
              <BestSalesPerson />

              <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))' }}>
                <div className="stat-card">
                  <div className="stat-content">
                    <h3 className="stat-title">Stock In Products</h3>
                    <p className="stat-value">{dashboardStats.loading ? '...' : dashboardStats.stockInCount}</p>
                    <p className="stat-subtitle">Total stock in transactions</p>
                  </div>
                  <div className="stat-icon" style={{ backgroundColor: '#4caf5020', color: '#4caf50' }}>
                    <i className="fas fa-box-open"></i>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-content">
                    <h3 className="stat-title">Stock Out Products</h3>
                    <p className="stat-value">{dashboardStats.loading ? '...' : dashboardStats.stockOutCount}</p>
                    <p className="stat-subtitle">Total stock out transactions</p>
                  </div>
                  <div className="stat-icon" style={{ backgroundColor: '#8c2f2f20', color: '#8c2f2f' }}>
                    <i className="fas fa-box"></i>
                  </div>
                </div>
                <div className="stat-card" 
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onNavigate) {
                      onNavigate('products');
                    }
                  }}
                >
                  <div className="stat-content">
                    <h3 className="stat-title">Products in Store</h3>
                    <p className="stat-value">{dashboardStats.loading ? '...' : dashboardStats.totalProducts}</p>
                    <p className="stat-subtitle">View-only list</p>
                  </div>
                  <div className="stat-icon" style={{ backgroundColor: '#99999920', color: '#666' }}>
                    <i className="fas fa-list"></i>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-content">
                    <h3 className="stat-title">Highest Selling Product</h3>
                    <p className="stat-value">
                      {dashboardStats.loading ? '...' : dashboardStats.highestSellingProduct 
                        ? dashboardStats.highestSellingProduct.productName || dashboardStats.highestSellingProduct.itemCode || 'N/A'
                        : 'N/A'}
                    </p>
                    <p className="stat-subtitle">
                      {dashboardStats.highestSellingProduct && !dashboardStats.loading
                        ? `${dashboardStats.highestSellingProduct.quantity || 0} units sold`
                        : 'Top performer'}
                    </p>
                  </div>
                  <div className="stat-icon" style={{ backgroundColor: '#dc354520', color: '#dc3545' }}>
                    <i className="fas fa-chart-line"></i>
                  </div>
                </div>
              </div>

          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">Notifications</div>
              <button className="link-btn" onClick={() => handleNavClick('notifications')}>View all</button>
            </div>
            {notifications.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                <i className="fas fa-bell-slash" style={{ fontSize: '24px', marginBottom: '10px', opacity: 0.5 }}></i>
                <p>No notifications</p>
              </div>
            ) : (
              notifications.slice(0, 5).map((notif) => (
                <div key={notif.id} className={`notif-row ${notif.notification_type === 'warning' ? 'alert' : notif.notification_type === 'success' ? 'success' : ''}`}>
                  <span className="dot"></span>
                  <div>
                    <div className="notif-title">{notif.title}</div>
                    <div className="notif-desc">{notif.message}</div>
                  </div>
                  <span className="time">{new Date(notif.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              ))
            )}
          </div>

              <footer className="dashboard-footer">
                <p>&copy; 2025 Anitha Stores. All rights reserved.</p>
              </footer>
            </>
          )}
        </main>
      </div>

      {showAttendanceModal && (
        <AttendanceModal
          type={attendanceType}
          onSuccess={handleAttendanceSuccess}
          onClose={handleAttendanceClose}
        />
      )}

      {showNotificationsPanel && userData?.id && (
        <NotificationsPanel
          onClose={() => setShowNotificationsPanel(false)}
          userId={userData.id}
          userType="staff"
        />
      )}
    </div>
  );
};

export default StaffDashboard;

